import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity, AlertTriangle, Building2, CheckCircle2, CreditCard,
  Database, HardDrive, Loader2, Plus, RefreshCw, Server, Shield, Trash2, Users,
  XCircle,
} from 'lucide-react';
import api, { errorText } from '../config';
import { useAuth } from '../context/AuthContext';

const TABS = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'workspaces', label: 'Workspaces', icon: Building2 },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'ops', label: 'Operations', icon: Server },
  { id: 'storage', label: 'Storage & cost', icon: HardDrive },
];

const PLAN_LABEL = {
  trial: 'Trial', screening: 'Screening', deal_team: 'Deal Team',
  firm: 'Firm', enterprise: 'Enterprise', internal: 'Internal', demo: 'Demo',
};

const STATUS_TONE = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  trialing: 'bg-sky-50 text-sky-700 border-sky-200',
  pending_invoice: 'bg-amber-50 text-amber-800 border-amber-200',
  expired: 'bg-rose-50 text-rose-700 border-rose-200',
  canceled: 'bg-rose-50 text-rose-700 border-rose-200',
};

function bytes(value) {
  const n = Number(value || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

function shortDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function Status({ value }) {
  const tone = STATUS_TONE[value] || 'bg-slate-50 text-slate-600 border-slate-200';
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize ${tone}`}>
      {(value || 'unknown').replace(/_/g, ' ')}
    </span>
  );
}

function Tile({ label, value, sub, tone = 'text-navy-900' }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">{label}</p>
      <p className={`mt-1.5 text-2xl font-bold ${tone}`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

/* ── Overview ─────────────────────────────────────────────────────── */
function Overview({ data, loading, onRefresh }) {
  if (loading && !data) return <Loading />;
  if (!data) return <Empty text="No platform data yet." />;
  const plans = Object.entries(data.plans || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">Read at {new Date(data.generated_at).toLocaleString()}</p>
        <button onClick={onRefresh} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-navy-900">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Workspaces" value={data.workspaces} sub={`${data.statuses?.trialing || 0} on trial`} />
        <Tile label="Users" value={data.users} sub="active memberships" />
        <Tile label="Deals" value={data.deals} sub={`${data.portcos} portfolio companies`} />
        <Tile label="Storage" value={bytes(data.storage_bytes)} sub={`${data.storage_objects} files`} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="text-sm font-bold text-navy-900">Plan mix</h3>
          <div className="mt-3 space-y-2">
            {plans.length === 0 && <p className="text-sm text-slate-500">No workspaces yet.</p>}
            {plans.map(([plan, count]) => (
              <div key={plan} className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{PLAN_LABEL[plan] || plan}</span>
                <span className="font-semibold text-navy-900">{count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="text-sm font-bold text-navy-900">
            Needs attention {data.workspaces_with_issues ? `(${data.workspaces_with_issues})` : ''}
          </h3>
          {data.issues?.length ? (
            <ul className="mt-3 space-y-2">
              {data.issues.map((issue) => (
                <li key={issue.tenant_id} className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2">
                  <p className="text-sm font-semibold text-navy-900">{issue.name}</p>
                  <ul className="mt-1 list-disc pl-4 text-xs text-amber-900">
                    {issue.issues.map((line) => <li key={line}>{line}</li>)}
                  </ul>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> Every workspace is healthy.
            </p>
          )}
          {data.orphan_count > 0 && (
            <p className="mt-3 text-xs text-slate-500">
              {data.orphan_count} stored files ({bytes(data.orphan_bytes)}) no longer belong to a deal.
            </p>
          )}
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="text-sm font-bold text-navy-900">Platform staff</h3>
        <p className="mt-1 text-xs text-slate-500">
          Set in configuration (CAPLY_SUPER_ADMINS). They see every workspace and can act on any of them.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {data.super_admins.map((email) => (
            <span key={email} className="inline-flex items-center gap-1.5 rounded-full border border-navy-200 bg-navy-50 px-2.5 py-1 text-xs font-semibold text-navy-900">
              <Shield className="h-3 w-3" /> {email}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Provisioning outcome (workspace owner / added user) ───────────── */
function ProvisionResult({ result, onClose }) {
  const signin = result.signin || {};
  const invite = result.invite || {};
  const [copied, setCopied] = useState(false);
  const password = result.temp_password || signin.temp_password || '';
  const email = result.owner_email || result.email || (result.member && result.member.email) || invite.to_email || '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold text-navy-900">
            {result.name ? `${result.name} is ready` : 'User added'}
          </p>
          {email && <p className="text-xs text-slate-600 mt-0.5">{email}</p>}
        </div>
        <button onClick={onClose} className="text-xs font-semibold text-slate-500 hover:text-navy-900">Dismiss</button>
      </div>

      <ul className="mt-3 space-y-1.5 text-xs">
        <li className="flex items-start gap-2">
          <span className={signin.error ? 'text-rose-600' : 'text-emerald-600'}>{signin.error ? '✕' : '✓'}</span>
          <span className="text-slate-700">
            {signin.error
              ? <>No sign-in account: {signin.error}</>
              : signin.existing
                ? 'Existing Caply account reused (their password is unchanged).'
                : 'Caply sign-in account created in the identity directory.'}
          </span>
        </li>
        <li className="flex items-start gap-2">
          <span className={invite.sent ? 'text-emerald-600' : 'text-amber-600'}>{invite.sent ? '✓' : '!'}</span>
          <span className="text-slate-700">
            {invite.sent
              ? `Invitation email sent${invite.from ? ` from ${invite.from}` : ''}.`
              : `No email sent — ${invite.reason || 'invitations were switched off'}`}
          </span>
        </li>
      </ul>

      {password && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Temporary password</p>
          <div className="mt-1 flex items-center gap-2">
            <code className="flex-1 font-mono text-sm text-navy-900 break-all">{password}</code>
            <button onClick={copy} className="rounded-md border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50">
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="mt-1 text-[11px] text-slate-500">
            Shown once. They choose their own password at first sign-in at www.caply4pe.com/login.
          </p>
        </div>
      )}
    </div>
  );
}

/* ── Workspaces ───────────────────────────────────────────────────── */
function overBy(workspace) {
  const licensed = Number(workspace?.seats_licensed || 0);
  const members = Number(workspace?.members || 0);
  if (licensed <= 0) return 0;
  return Math.max(0, members - licensed);
}

function Workspaces({ workspaces, plans, loading, onCreate, onPlan, onRename, onDelete, onSeats, onTrial, busy }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', owner_email: '', owner_name: '', plan_id: 'trial', seats: 3, owner_initial_password: '', send_invite: true });
  const [outcome, setOutcome] = useState(null);
  const [editing, setEditing] = useState(null);
  const [seatEdit, setSeatEdit] = useState(null);
  const [seatValue, setSeatValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [trialFor, setTrialFor] = useState(null);
  const [confirmName, setConfirmName] = useState('');

  if (loading && !workspaces) return <Loading />;

  const submit = async () => {
    const res = await onCreate(form);
    if (res) {
      setOutcome(res);
      setCreating(false);
      setForm({ name: '', owner_email: '', owner_name: '', plan_id: 'trial', seats: 3, owner_initial_password: '', send_invite: true });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{workspaces?.length || 0} workspaces on the platform</p>
        <button
          onClick={() => setCreating((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-bold text-navy-950 hover:bg-brand-700"
        >
          <Plus className="h-3.5 w-3.5" /> New workspace
        </button>
      </div>

      {creating && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-bold text-navy-900">Provision a workspace</h3>
          <p className="mt-1 text-xs text-slate-500">
            Creates the workspace, its plan and its first admin. The owner gets a real Caply sign-in account and an
            invitation email. Leave the password blank and one is generated for you.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field label="Workspace name">
              <input className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Northline Capital" />
            </Field>
            <Field label="Owner email">
              <input className="form-input" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} placeholder="partner@northline.com" />
            </Field>
            <Field label="Owner name">
              <input className="form-input" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} placeholder="Optional" />
            </Field>
            <Field label="Initial password (optional)">
              <input
                className="form-input font-mono text-xs"
                type="text"
                value={form.owner_initial_password}
                onChange={(e) => setForm({ ...form, owner_initial_password: e.target.value })}
                placeholder="Leave blank to generate one"
              />
              <p className={`mt-1 text-[11px] ${form.owner_initial_password && form.owner_initial_password.trim().length < 8 ? 'text-rose-600 font-medium' : 'text-slate-500'}`}>
                {form.owner_initial_password && form.owner_initial_password.trim().length < 8
                  ? 'That is too short — use at least 8 characters, or leave it blank to generate one.'
                  : 'At least 8 characters. They change it at first sign-in.'}
              </p>
            </Field>
            <Field label="Starting plan">
              <div className="flex gap-2">
                <select className="form-input" value={form.plan_id} onChange={(e) => setForm({ ...form, plan_id: e.target.value })}>
                  {(plans || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <input
                  type="number" min="1" className="form-input w-24" value={form.seats}
                  onChange={(e) => setForm({ ...form, seats: Number(e.target.value) })} title="Seats"
                />
              </div>
            </Field>
          </div>
          <label className="mt-3 inline-flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={form.send_invite} onChange={(e) => setForm({ ...form, send_invite: e.target.checked })} />
            Email the owner their sign-in details
          </label>
          <div className="mt-3 flex gap-2">
            <button
            disabled={busy || Boolean(form.owner_initial_password && form.owner_initial_password.trim().length < 8)}
            onClick={submit}
            className="rounded-lg bg-navy-900 px-3 py-1.5 text-xs font-bold text-white hover:bg-navy-800 disabled:opacity-50"
          >
              {busy ? 'Creating…' : 'Create workspace'}
            </button>
            <button onClick={() => setCreating(false)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600">Cancel</button>
          </div>
        </div>
      )}

      {outcome && <ProvisionResult result={outcome} onClose={() => setOutcome(null)} />}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-2.5 font-semibold">Workspace</th>
              <th className="px-3 py-2.5 font-semibold">Plan</th>
              <th className="px-3 py-2.5 font-semibold">Status</th>
              <th className="px-3 py-2.5 font-semibold text-right">Seats</th>
              <th className="px-3 py-2.5 font-semibold text-right">Users</th>
              <th className="px-3 py-2.5 font-semibold text-right">Deals</th>
              <th className="px-3 py-2.5 font-semibold text-right">Storage</th>
              <th className="px-3 py-2.5 font-semibold">Renews</th>
              <th className="px-3 py-2.5 font-semibold" />
            </tr>
          </thead>
          <tbody>
            {(workspaces || []).map((w) => (
              <tr key={w.tenant_id} className="border-b border-slate-100 last:border-0 align-top">
                <td className="px-4 py-3">
                  <p className="font-semibold text-navy-900">{w.name}</p>
                  <p className="text-[11px] text-slate-400">{w.tenant_id}</p>
                  {w.issues?.length > 0 && (
                    <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700">
                      <AlertTriangle className="h-3 w-3" /> {w.issues[0]}
                      {w.issues.length > 1 && ` +${w.issues.length - 1}`}
                    </p>
                  )}
                </td>
                <td className="px-3 py-3">
                  <select
                    className="rounded-md border border-slate-200 px-1.5 py-1 text-xs font-semibold text-navy-900"
                    value={w.plan_id}
                    onChange={(e) => onPlan(w, e.target.value)}
                  >
                    {(plans || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </td>
                <td className="px-3 py-3"><Status value={w.status} /></td>
                <td className="px-3 py-3 text-right">
                  {seatEdit === w.tenant_id ? (
                    <span className="inline-flex items-center gap-1">
                      <input
                        type="number"
                        min="1"
                        autoFocus
                        className="w-16 rounded-md border border-slate-300 px-1.5 py-1 text-right text-xs"
                        value={seatValue}
                        onChange={(e) => setSeatValue(e.target.value)}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter') {
                            const ok = await onSeats(w, Number(seatValue));
                            if (ok) setSeatEdit(null);
                          }
                          if (e.key === 'Escape') setSeatEdit(null);
                        }}
                      />
                      <button
                        disabled={busy || !seatValue}
                        onClick={async () => {
                          const ok = await onSeats(w, Number(seatValue));
                          if (ok) setSeatEdit(null);
                        }}
                        className="rounded-md bg-navy-900 px-1.5 py-1 text-[11px] font-bold text-white disabled:opacity-50"
                      >
                        Save
                      </button>
                      <button onClick={() => setSeatEdit(null)} className="text-[11px] font-semibold text-slate-500">
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <button
                      onClick={() => { setSeatEdit(w.tenant_id); setSeatValue(String(w.seats_licensed ?? '')); }}
                      className="text-right text-slate-700 underline decoration-slate-300 decoration-dotted underline-offset-2 hover:text-navy-900"
                      title="Change how many seats this workspace is licensed for"
                    >
                      {Number(w.seats_licensed) > 0 ? w.seats_licensed : '—'}
                    </button>
                  )}
                </td>
                <td className={`px-3 py-3 text-right ${overBy(w) > 0 ? 'font-semibold text-amber-700' : 'text-slate-600'}`}>
                  {w.members}
                  {overBy(w) > 0 && (
                    <span className="ml-1 text-[10px] font-semibold">+{overBy(w)} over</span>
                  )}
                </td>
                <td className="px-3 py-3 text-right text-slate-600">{w.deals}</td>
                <td className="px-3 py-3 text-right text-slate-600">{bytes(w.storage_bytes)}</td>
                <td className="px-3 py-3 whitespace-nowrap text-slate-600">
                  {w.status === 'trialing' ? shortDate(w.trial_ends_at) : shortDate(w.current_period_end)}
                </td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  {w.plan_id === 'trial' && (
                    <button onClick={() => setTrialFor(trialFor === w.tenant_id ? null : w.tenant_id)} className="mr-3 text-xs font-semibold text-slate-500 hover:text-navy-900">
                      Trial
                    </button>
                  )}
                  <button onClick={() => setEditing(editing === w.tenant_id ? null : w.tenant_id)} className="text-xs font-semibold text-slate-500 hover:text-navy-900">
                    Rename
                  </button>
                  <button onClick={() => { setDeleteTarget(w); setConfirmName(''); }} className="ml-3 text-xs font-semibold text-rose-600 hover:text-rose-700">
                    Delete
                  </button>
                  {editing === w.tenant_id && (
                    <RenameInline workspace={w} onRename={onRename} done={() => setEditing(null)} />
                  )}
                  {trialFor === w.tenant_id && (
                    <TrialInline
                      workspace={w}
                      onTrial={onTrial}
                      done={() => setTrialFor(null)}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-950/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5">
            <h3 className="text-base font-bold text-navy-900">Delete {deleteTarget.name}?</h3>
            <p className="mt-1 text-sm text-slate-600">
              This removes every deal, document, request list, report and stored file in the workspace.
              It cannot be undone. Type the workspace name to confirm.
            </p>
            <input className="form-input mt-3" value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={deleteTarget.name} />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600">Cancel</button>
              <button
                disabled={busy || confirmName.trim() !== deleteTarget.name}
                onClick={async () => { if (await onDelete(deleteTarget, confirmName)) setDeleteTarget(null); }}
                className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-40"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete workspace
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TrialInline({ workspace, onTrial, done }) {
  const ends = workspace.trial_ends_at ? shortDate(workspace.trial_ends_at) : 'no end date';
  const lapsed = workspace.trial_ends_at ? new Date(workspace.trial_ends_at) <= new Date() : false;
  const [confirming, setConfirming] = useState(false);
  const act = async (action, days) => {
    const ok = await onTrial(workspace, action, days);
    if (ok) { setConfirming(false); done(); }
  };
  return (
    <div className="mt-2 w-56 rounded-lg border border-slate-200 bg-slate-50 p-2 text-left">
      <p className="text-[11px] text-slate-500">
        Trial {lapsed ? 'ran out' : 'ends'} {ends}
      </p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        <button onClick={() => act('extend', 7)} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-navy-900 hover:border-navy-900">
          +7 days
        </button>
        <button onClick={() => act('extend', 14)} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-navy-900 hover:border-navy-900">
          +14 days
        </button>
        <button onClick={() => act('restart', 14)} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-navy-900 hover:border-navy-900">
          Restart 14
        </button>
        {confirming ? (
          <button onClick={() => act('end', null)} className="rounded-md bg-rose-600 px-2 py-1 text-[11px] font-bold text-white">
            Yes, end it
          </button>
        ) : (
          <button onClick={() => setConfirming(true)} className="rounded-md border border-rose-200 px-2 py-1 text-[11px] font-semibold text-rose-600 hover:border-rose-400">
            End trial now
          </button>
        )}
      </div>
      {confirming && (
        <p className="mt-1.5 text-[11px] text-rose-600">
          The workspace becomes read only. Deals and files stay where they are.
        </p>
      )}
    </div>
  );
}

function RenameInline({ workspace, onRename, done }) {
  const [value, setValue] = useState(workspace.name);
  return (
    <div className="mt-2 flex gap-1.5">
      <input className="form-input w-40 text-xs" value={value} onChange={(e) => setValue(e.target.value)} />
      <button
        onClick={async () => { if (await onRename(workspace, value)) done(); }}
        className="rounded-md bg-navy-900 px-2 py-1 text-[11px] font-bold text-white"
      >
        Save
      </button>
    </div>
  );
}

/* ── Users ────────────────────────────────────────────────────────── */
function UsersTab({ users, workspaces, loading, onAdd, onUpdate, onRemove, onPassword, busy }) {
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ email: '', tenant_id: '', display_name: '', role: 'Analyst', create_signin: true, initial_password: '', send_invite: true });
  const [result, setResult] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users || [];
    return (users || []).filter((u) =>
      [u.email, u.display_name, u.workspace, u.role].some((v) => (v || '').toLowerCase().includes(q)),
    );
  }, [users, query]);

  if (loading && !users) return <Loading />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <input className="form-input max-w-xs" placeholder="Search users" value={query} onChange={(e) => setQuery(e.target.value)} />
        <button
          onClick={() => setAdding((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-bold text-navy-950 hover:bg-brand-700"
        >
          <Plus className="h-3.5 w-3.5" /> Add user
        </button>
      </div>

      {adding && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-bold text-navy-900">Add someone to a workspace</h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Email">
              <input className="form-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@firm.com" />
            </Field>
            <Field label="Name">
              <input className="form-input" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Optional" />
            </Field>
            <Field label="Workspace">
              <select className="form-input" value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}>
                <option value="">Choose…</option>
                {(workspaces || []).map((w) => <option key={w.tenant_id} value={w.tenant_id}>{w.name}</option>)}
              </select>
            </Field>
            <Field label="Role">
              <select className="form-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option>Admin</option><option>Analyst</option><option>Viewer</option>
              </select>
            </Field>
          </div>
          <Field label="Initial password (optional)">
            <input
              className="form-input font-mono text-xs"
              value={form.initial_password}
              onChange={(e) => setForm({ ...form, initial_password: e.target.value })}
              placeholder="Leave blank to generate one"
            />
            {form.initial_password && form.initial_password.trim().length < 8 && (
              <p className="mt-1 text-[11px] font-medium text-rose-600">
                At least 8 characters, or leave it blank to generate one.
              </p>
            )}
          </Field>
          <label className="mt-3 inline-flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={form.create_signin} onChange={(e) => setForm({ ...form, create_signin: e.target.checked })} />
            Create their Caply sign-in account if it does not exist
          </label>
          <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={form.send_invite} onChange={(e) => setForm({ ...form, send_invite: e.target.checked })} />
            Email them their sign-in details
          </label>
          <div className="mt-3 flex gap-2">
            <button
              disabled={busy || !form.email || !form.tenant_id}
              onClick={async () => {
                const ok = await onAdd(form);
                if (ok) { setAdding(false); setForm({ email: '', tenant_id: '', display_name: '', role: 'Analyst', create_signin: true, initial_password: '', send_invite: true }); }
              }}
              className="rounded-lg bg-navy-900 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-40"
            >
              {busy ? 'Adding…' : 'Add user'}
            </button>
            <button onClick={() => setAdding(false)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600">Cancel</button>
          </div>
        </div>
      )}

      {result && <ProvisionResult result={result} onClose={() => setResult(null)} />}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-2.5 font-semibold">User</th>
              <th className="px-3 py-2.5 font-semibold">Workspace</th>
              <th className="px-3 py-2.5 font-semibold">Role</th>
              <th className="px-3 py-2.5 font-semibold">Status</th>
              <th className="px-3 py-2.5 font-semibold">Joined</th>
              <th className="px-3 py-2.5 font-semibold" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 last:border-0">
                <td className="px-4 py-3">
                  <p className="font-semibold text-navy-900">{u.display_name || u.email.split('@')[0]}</p>
                  <p className="text-xs text-slate-500">{u.email}</p>
                  {u.is_super_admin && (
                    <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-navy-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-400">
                      <Shield className="h-2.5 w-2.5" /> Platform staff
                    </span>
                  )}
                </td>
                <td className="px-3 py-3">
                  <select
                    className="rounded-md border border-slate-200 px-1.5 py-1 text-xs text-navy-900"
                    value={u.tenant_id}
                    onChange={(e) => onUpdate(u, { tenant_id: e.target.value })}
                  >
                    {(workspaces || []).map((w) => <option key={w.tenant_id} value={w.tenant_id}>{w.name}</option>)}
                  </select>
                </td>
                <td className="px-3 py-3">
                  <select
                    className="rounded-md border border-slate-200 px-1.5 py-1 text-xs text-navy-900"
                    value={u.role}
                    onChange={(e) => onUpdate(u, { role: e.target.value })}
                  >
                    <option>Admin</option><option>Analyst</option><option>Viewer</option>
                  </select>
                </td>
                <td className="px-3 py-3"><Status value={u.status} /></td>
                <td className="px-3 py-3 whitespace-nowrap text-slate-600">{shortDate(u.created_at)}</td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => onPassword && onPassword(u)}
                    className="text-xs font-semibold text-slate-600 hover:text-navy-900 mr-3"
                    title="Set a new password and show it once"
                  >
                    Set password
                  </button>
                  {!u.is_super_admin && (
                    <button onClick={() => onRemove(u)} className="text-xs font-semibold text-rose-600 hover:text-rose-700">Remove</button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-slate-500">No users match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Billing ──────────────────────────────────────────────────────── */
function BillingTab({ issues, loading, onResolve, busy }) {
  if (loading && !issues) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="text-sm font-bold text-navy-900">How a billing fix works</h3>
        <p className="mt-1 text-xs text-slate-500">
          Each action writes the same commercial record a real purchase produces, so the workspace sees the change
          immediately and it appears in its own billing history. Nothing here charges a card: Caply is invoiced.
        </p>
      </div>
      {(!issues || issues.length === 0) && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800">
          <CheckCircle2 className="mb-1 inline h-4 w-4" /> No workspace needs a billing decision right now.
        </div>
      )}
      {(issues || []).map((issue) => (
        <div key={issue.tenant_id} className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-semibold text-navy-900">{issue.name}</p>
              <p className="text-xs text-slate-500">
                {PLAN_LABEL[issue.plan_id] || issue.plan_id} · {issue.seats_licensed} seats ·{' '}
                {issue.status === 'trialing' ? `trial ends ${shortDate(issue.trial_ends_at)}` : `period ends ${shortDate(issue.current_period_end)}`}
              </p>
            </div>
            <Status value={issue.status} />
          </div>
          <ul className="mt-2 space-y-1 text-xs text-amber-800">
            {issue.issues.map((line) => <li key={line}>• {line}</li>)}
          </ul>
          {issue.invoice && (
            <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
              Invoice requested: {PLAN_LABEL[issue.invoice.plan_id] || issue.invoice.plan_id}, {issue.invoice.seats} seats
              {issue.invoice.amount_usd ? `, $${Number(issue.invoice.amount_usd).toLocaleString()} a year` : ''}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {issue.invoice && (
              <ActionButton busy={busy} onClick={() => onResolve(issue, { action: 'mark_paid' })} tone="primary">
                Mark invoice paid
              </ActionButton>
            )}
            <ActionButton busy={busy} onClick={() => onResolve(issue, { action: 'extend_trial', period_days: 14 })}>
              Extend trial 14 days
            </ActionButton>
            <ActionButton busy={busy} onClick={() => onResolve(issue, { action: 'comp', plan_id: 'internal' })}>
              Comp (internal plan)
            </ActionButton>
            <ActionButton busy={busy} onClick={() => onResolve(issue, { action: 'activate', plan_id: 'deal_team', period_days: 365, seats: 3 })}>
              Activate Deal Team
            </ActionButton>
            <ActionButton busy={busy} onClick={() => onResolve(issue, { action: 'cancel' })} tone="danger">
              Cancel plan
            </ActionButton>
          </div>
        </div>
      ))}
    </div>
  );
}

function ActionButton({ children, onClick, busy, tone = 'plain' }) {
  const cls = tone === 'primary'
    ? 'bg-brand-600 text-navy-950 hover:bg-brand-700'
    : tone === 'danger'
      ? 'border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100'
      : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50';
  return (
    <button disabled={busy} onClick={onClick} className={`rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-40 ${cls}`}>
      {children}
    </button>
  );
}

/* ── Operations ───────────────────────────────────────────────────── */
function OpsTab({ logs, health, loading, onRefresh }) {
  if (loading && !logs) return <Loading />;
  const kindTone = {
    staff: 'bg-navy-900 text-brand-400',
    billing: 'bg-amber-100 text-amber-800',
    data: 'bg-slate-100 text-slate-600',
  };
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-navy-900">Platform health</h3>
          <button onClick={onRefresh} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-navy-900">
            <RefreshCw className="h-3.5 w-3.5" /> Recheck
          </button>
        </div>
        {health?.environment_binding?.dev_bound?.length > 0 && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            <p className="text-xs font-semibold text-amber-800">
              This deployment still uses the dev resources
              {health.environment_binding.environment === 'production' ? ' in production' : ''}
            </p>
            <p className="mt-0.5 text-[11px] text-amber-700">
              {health.environment_binding.dev_bound.join(' · ')}
            </p>
            <p className="mt-1 text-[11px] text-amber-700">
              Fine while there is one environment. Give the next environment its own values before it carries real customer data.
            </p>
          </div>
        )}
        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {Object.entries(health?.checks || {}).map(([name, check]) => (
            <div key={name} className={`rounded-lg border px-3 py-2 ${check.ok ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`}>
              <p className="flex items-center gap-1.5 text-xs font-bold capitalize text-navy-900">
                {check.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="h-3.5 w-3.5 text-rose-600" />}
                {name.replace(/_/g, ' ')}
              </p>
              {check.error && <p className="mt-1 text-[11px] text-rose-700">{check.error}</p>}
              {check.container && <p className="mt-1 text-[11px] text-slate-500">{check.container} · {check.objects} files</p>}
              {check.version && <p className="mt-1 text-[11px] text-slate-500">build {check.version} · {check.environment}</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="text-sm font-bold text-navy-900">Recent activity</h3>
        <p className="mt-1 text-xs text-slate-500">
          Staff actions, billing events and the records touched most recently, newest first.
          {logs?.counts && ` ${logs.counts.staff_actions} staff actions · ${logs.counts.billing_events} billing events · ${logs.counts.records_touched} records in the window.`}
        </p>
        <ol className="mt-3 divide-y divide-slate-100">
          {(logs?.entries || []).map((entry, i) => (
            <li key={`${entry.at}-${i}`} className="flex items-start gap-3 py-2">
              <span className={`mt-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${kindTone[entry.kind] || kindTone.data}`}>
                {entry.kind}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-navy-900">
                  <span className="font-semibold">{entry.title}</span>
                  {entry.workspace ? <span className="text-slate-500"> · {entry.workspace}</span> : null}
                </p>
                <p className="text-[11px] text-slate-400">
                  {entry.at ? new Date(entry.at).toLocaleString() : ''}
                  {entry.actor ? ` · ${entry.actor}` : ''}
                  {entry.detail?.amount_usd ? ` · $${Number(entry.detail.amount_usd).toLocaleString()}` : ''}
                </p>
              </div>
            </li>
          ))}
          {(!logs?.entries || logs.entries.length === 0) && (
            <li className="py-4 text-sm text-slate-500">Nothing recorded yet.</li>
          )}
        </ol>
      </div>
    </div>
  );
}

/* ── Storage & cost ───────────────────────────────────────────────── */
function StorageTab({ storage, cost, loading, onPurge, busy }) {
  const [dryRun, setDryRun] = useState(null);
  if (loading && !storage) return <Loading />;
  const azure = cost?.azure || {};
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Stored files" value={storage?.object_count ?? 0} sub={bytes(storage?.total_bytes)} />
        <Tile label="Orphaned files" value={storage?.orphan_count ?? 0} sub={bytes(storage?.orphan_bytes)} tone={storage?.orphan_count ? 'text-amber-700' : 'text-navy-900'} />
        <Tile
          label="Azure spend (month)"
          value={azure.available ? `$${Number(azure.month_to_date).toLocaleString()}` : 'Not available'}
          sub={azure.available ? azure.currency : 'see note below'}
          tone={azure.available ? 'text-navy-900' : 'text-slate-500'}
        />
        <Tile label="Documents" value={cost?.usage?.documents ?? 0} sub="drafted documents and reports" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="flex items-center gap-2 text-sm font-bold text-navy-900">
          <Database className="h-4 w-4" /> Storage by workspace
        </h3>
        <p className="mt-1 text-xs text-slate-500">Measured from the storage container: {storage?.container}.</p>
        <div className="mt-3 space-y-2">
          {(storage?.by_workspace || []).map((row) => {
            const pct = storage?.total_bytes ? Math.round((row.bytes / storage.total_bytes) * 100) : 0;
            return (
              <div key={row.tenant_id}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-navy-900">{row.name || row.tenant_id}</span>
                  <span className="text-slate-500">{row.objects} files · {bytes(row.bytes)}</span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-brand-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
          {(storage?.by_workspace || []).length === 0 && <p className="text-sm text-slate-500">No files stored yet.</p>}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="text-sm font-bold text-navy-900">Orphaned files</h3>
        <p className="mt-1 text-xs text-slate-500">
          Files whose deal no longer exists. Deleting them frees space and cannot touch a live deal:
          the check runs against every deal id in the platform first.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <ActionButton busy={busy} onClick={async () => setDryRun(await onPurge(true))}>Check what would be removed</ActionButton>
          <ActionButton
            busy={busy || !storage?.orphan_count}
            tone="danger"
            onClick={async () => { const res = await onPurge(false); setDryRun(res); }}
          >
            Delete orphaned files
          </ActionButton>
        </div>
        {dryRun && (
          <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700">
            {dryRun.dry_run
              ? `${dryRun.would_delete} files (${bytes(dryRun.bytes)}) would be deleted.`
              : `${dryRun.deleted} files deleted, ${bytes(dryRun.bytes_freed)} freed${dryRun.failed?.length ? `, ${dryRun.failed.length} failed` : ''}.`}
            {dryRun.objects?.length ? (
              <ul className="mt-1 max-h-32 overflow-y-auto font-mono text-[11px] text-slate-500">
                {dryRun.objects.slice(0, 20).map((name) => <li key={name}>{name}</li>)}
              </ul>
            ) : null}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="text-sm font-bold text-navy-900">Azure cost</h3>
        {azure.available ? (
          <>
            <p className="mt-1 text-xs text-slate-500">{azure.source}</p>
            <div className="mt-3 space-y-2">
              {(azure.by_service || []).map((row) => (
                <div key={row.service} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{row.service}</span>
                  <span className="font-semibold text-navy-900">${Number(row.cost).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="mt-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {azure.reason || 'Azure spend is not readable from the API right now.'}
          </p>
        )}
        <p className="mt-3 text-[11px] text-slate-400">{cost?.windows_azure_note}</p>
      </div>
    </div>
  );
}

/* ── shell ────────────────────────────────────────────────────────── */
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      {children}
    </label>
  );
}

function Loading() {
  return (
    <div className="flex items-center gap-2 py-10 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading platform data…
    </div>
  );
}

function Empty({ text }) {
  return <p className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">{text}</p>;
}

export default function SuperAdmin() {
  const { user } = useAuth();
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [workspaces, setWorkspaces] = useState(null);
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState(null);
  const [issues, setIssues] = useState(null);
  const [logs, setLogs] = useState(null);
  const [health, setHealth] = useState(null);
  const [storage, setStorage] = useState(null);
  const [cost, setCost] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ov, ws, pl, us, bi, lg, he, st, co] = await Promise.all([
        api.get('/api/superadmin/overview'),
        api.get('/api/superadmin/workspaces'),
        api.get('/api/superadmin/plans'),
        api.get('/api/superadmin/users'),
        api.get('/api/superadmin/billing'),
        api.get('/api/superadmin/logs?limit=60'),
        api.get('/api/superadmin/health'),
        api.get('/api/superadmin/storage'),
        api.get('/api/superadmin/cost'),
      ]);
      setOverview(ov.data);
      setWorkspaces(ws.data.workspaces);
      setPlans(pl.data);
      setUsers(us.data.users);
      setIssues(bi.data.issues);
      setLogs(lg.data);
      setHealth(he.data);
      setStorage(st.data);
      setCost(co.data);
    } catch (err) {
      setError(errorText(err, 'Could not load the console.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.is_super_admin) load();
  }, [user?.is_super_admin, load]);

  const run = async (fn, successMessage) => {
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      const result = await fn();
      if (successMessage) setNotice(typeof successMessage === 'function' ? successMessage(result) : successMessage);
      await load();
      return result;
    } catch (err) {
      setError(errorText(err, 'That did not work.'));
      return null;
    } finally {
      setBusy(false);
    }
  };

  if (!user?.is_super_admin) {
    return (
      <div className="page-container max-w-3xl">
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h1 className="flex items-center gap-2 text-lg font-bold text-navy-900">
            <Shield className="h-5 w-5" /> Platform console
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            This console is for Caply platform staff. Your account ({user?.email || 'signed in'}) is a workspace
            administrator, not platform staff, so nothing here is available to you.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container w-full max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-navy-900">
            <Shield className="h-5 w-5" /> Platform console
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Every workspace, user, plan, file and log on Caply. Signed in as {user.email}.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-navy-900"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh all
        </button>
      </div>

      <nav className="mt-4 flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-semibold transition ${
              tab === id
                ? 'border-brand-500 text-navy-900'
                : 'border-transparent text-slate-500 hover:text-navy-900'
            }`}
          >
            <Icon className="h-4 w-4" /> {label}
            {id === 'billing' && issues?.length > 0 && (
              <span className="ml-1 rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white">{issues.length}</span>
            )}
          </button>
        ))}
      </nav>

      {notice && (
        <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{notice}</div>
      )}
      {error && (
        <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{error}</div>
      )}

      <div className="mt-5">
        {tab === 'overview' && <Overview data={overview} loading={loading} onRefresh={load} />}
        {tab === 'workspaces' && (
          <Workspaces
            workspaces={workspaces}
            plans={plans}
            loading={loading}
            busy={busy}
            onCreate={async (form) => {
              const res = await run(() => api.post('/api/superadmin/workspaces', form), null);
              return res ? { ...res.data, owner_email: form.owner_email } : null;
            }}
            onPlan={(w, planId) => run(
              () => api.patch(`/api/superadmin/workspaces/${w.tenant_id}`, { plan_id: planId }),
              `${w.name} is now on ${PLAN_LABEL[planId] || planId}.`,
            )}
            onSeats={(w, seats) => run(
              () => api.post(`/api/superadmin/workspaces/${w.tenant_id}/seats`, { seats }),
              (res) => res.data?.note || 'Seat count updated.'
            )}
            onTrial={(w, action, days) => run(
              () => api.post(`/api/superadmin/workspaces/${w.tenant_id}/trial`, { action, days }),
              (res) => res.data?.note || 'Trial updated.'
            )}
            onRename={(w, name) => run(
              () => api.patch(`/api/superadmin/workspaces/${w.tenant_id}`, { name }),
              (res) => `Renamed to ${res.data.name || name}.`,
            )}
            onDelete={(w, confirm) => run(
              () => api.delete(`/api/superadmin/workspaces/${w.tenant_id}`, { params: { confirm } }),
              (res) => `${w.name} deleted: ${res.data.deleted.documents} records and ${res.data.deleted.blobs} files removed${res.data.verified_empty ? ', nothing left behind' : ', some records could not be removed'}.`,
            )}
          />
        )}
        {tab === 'users' && (
          <UsersTab
            users={users}
            workspaces={workspaces}
            loading={loading}
            busy={busy}
            onAdd={async (form) => {
              const res = await run(() => api.post('/api/superadmin/users', form), null);
              if (res) setResult({ ...res.data, email: form.email });
              return Boolean(res);
            }}
            onUpdate={(u, patch) => run(
              () => api.patch(`/api/superadmin/users/${u.id}`, patch),
              `${u.email} updated.`,
            )}
            onPassword={async (u) => {
              const res = await run(() => api.post(`/api/superadmin/users/${u.id}/password`, {}), null);
              if (res) setResult({ ...res.data, email: u.email });
              return Boolean(res);
            }}
            onRemove={(u) => run(
              () => api.delete(`/api/superadmin/users/${u.id}`),
              (res) => `${res.data.email} removed. ${res.data.signin}`,
            )}
          />
        )}
        {tab === 'billing' && (
          <BillingTab
            issues={issues}
            loading={loading}
            busy={busy}
            onResolve={(issue, payload) => run(
              () => api.post(`/api/superadmin/billing/${issue.tenant_id}`, payload),
              (res) => `${issue.name}: ${res.data.outcome}`,
            )}
          />
        )}
        {tab === 'ops' && <OpsTab logs={logs} health={health} loading={loading} onRefresh={load} />}
        {tab === 'storage' && (
          <StorageTab
            storage={storage}
            cost={cost}
            loading={loading}
            busy={busy}
            onPurge={(isDryRun) => run(
              () => api.post('/api/superadmin/storage/purge', { dry_run: isDryRun }),
              null,
            )}
          />
        )}
      </div>
    </div>
  );
}
